import { Footer as KobleFooter } from '@innovationnorway/cl-react';
import { FC, Fragment, ReactNode } from 'react';
import { apiClient, ApiSchema } from '../lib/query/apiClient';

type ContentModel = ApiSchema<'PortableTextBlockDto'>;
type ChildModel = ApiSchema<'PortableTextSpanDto'>;
type FooterBlockModel = ApiSchema<'FooterBlockDto'>;
type ItemModel = ApiSchema<'FooterItemDto'>;
type LinkModel = ApiSchema<'LinkDto'>;
type SocialsBlockModel = ApiSchema<'SocialsBlockDto'>;
type MappedLink = { url: string; title: string };

const mapLinkModel = (link?: LinkModel | null) => {
  if (!link?.url || !link?.title) return undefined;
  return { url: link.url, title: link.title };
};

const mapItemModel = (items: ItemModel[] | undefined | null, renderContent: (content: ContentModel[]) => ReactNode) =>
  items?.map((item) => ({
    link: mapLinkModel(item.link),
    content: renderContent(item.content ?? []),
  }));

const mapFooterBlockModel = (model: FooterBlockModel, renderContent: (content: ContentModel[]) => ReactNode) => ({
  heading: model.heading ?? '',
  items: mapItemModel(model.items, renderContent),
  showMoreLink: mapLinkModel(model.showMoreLink),
});

const mapSocialsBlockModel = (model: SocialsBlockModel | undefined | null, renderContent: (content: ContentModel[]) => ReactNode) => {
  if (!model) return undefined;
  return {
    heading: model.heading ?? '',
    items: mapItemModel(model.items, renderContent),
    socialMediaLinks: model.socialMediaLinks?.map(mapLinkModel).filter((l): l is MappedLink => l !== undefined),
  };
};

const renderChild = (child: ChildModel, block: ContentModel) => {
  let textElement: ReactNode = child.text;

  if (child.marks?.includes('strong')) {
    textElement = <strong>{textElement}</strong>;
  }
  if (child.marks?.includes('em')) {
    textElement = <em>{textElement}</em>;
  }

  const linkMark = child.marks?.find((mark) => block.markDefs?.some((def) => def.key === mark && def.type === 'link'));
  const href = block.markDefs?.find((def) => def.key === linkMark)?.href;
  if (href) {
    textElement = (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {textElement}
      </a>
    );
  }

  return <Fragment key={child.key}>{textElement}</Fragment>;
};

const renderBlock = (block: ContentModel) => {
  const children = block.children?.map((child) => renderChild(child, block));

  switch (block.style) {
    case 'h1':
      return <h1 key={block.key}>{children}</h1>;
    case 'h2':
      return <h2 key={block.key}>{children}</h2>;
    case 'h3':
      return <h3 key={block.key}>{children}</h3>;
    case 'normal':
    default:
      return <p key={block.key}>{children}</p>;
  }
};

const renderContent = (content: ContentModel[]) => content.map(renderBlock);

export const Footer: FC = () => {
  const { data } = apiClient.useQuery('get', '/api/v1/Session/GetFooterData');
  if (!data) return null;

  return (
    <KobleFooter
      bottomContent={renderContent(data.bottomContent ?? [])}
      footerBlocks={data.footerBlocks?.map((model) => mapFooterBlockModel(model, renderContent))}
      socialsBlock={mapSocialsBlockModel(data.socialsBlock, renderContent)}
      theme={data.theme === 'light' ? 'light' : 'dark'}
      goToTop={data.displayBackToTop}
      displayIllustration={data.displayIllustration}
    />
  );
};
